import React, { useEffect, useState } from "react";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import CustomError from "../../../../utils/customError";
import { useFormik } from "formik";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import Swal from "sweetalert2";
import { TempleDetails, TempleEdit } from "../../services/temple.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

const EditTemple = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams(); // URL param
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [templeImageUrl, setTempleImageUrl] = useState(""); // For display
  const [newImageSelected, setNewImageSelected] = useState(false);
  const [originalTempleName, setOriginalTempleName] = useState(""); // For alt

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    { title: t("Temple"), url: "/admin/temples/list/1" },
    { title: t("Edit"), url: "" },
  ];

  const validationSchema = Yup.object().shape({
    templeName: Yup.string().required("Temple name is required"),
    templeLocation: Yup.string().required("Temple location is required"),
    templeDesc: Yup.string().required("Temple description is required"),
    templeImage: Yup.mixed()
      .nullable()
      .notRequired()
      .test("templeImage", "Only JPG, JPEG, PNG files are allowed", (value) => {
        if (!value) return true;
        return ["image/jpeg", "image/jpg", "image/png"].includes(value.type);
      }),
  });

  const formik = useFormik({
    initialValues: {
      templeName: "",
      templeLocation: "",
      templeDesc: "",
      templeImage: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      globalLoader(true);
      setSubmitted(true);

      const formData = new FormData();
      formData.append("o_id", id);
      formData.append("templeName", values.templeName);
      formData.append("templeLocation", values.templeLocation);
      formData.append("templeDesc", values.templeDesc);

      // Only append new image if selected
      if (newImageSelected && values.templeImage) {
        formData.append("templeImage", values.templeImage);
      }

      TempleEdit(formData)
        .then((res) => {
          globalLoader(false);
          setSubmitted(false);
          if (res.success) {
            Swal.fire({
              icon: "success",
              text: res.message,
              ...SWAL_SETTINGS,
            });
            navigate("/admin/temples/list/1");
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(res),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((err) => {
          console.log("Error editing temple:", err);
          globalLoader(false);
          setSubmitted(false);
        });
    },
  });

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    TempleDetails(id)
      .then((res) => {
        if (res.success) {
          const data = res.data;
          console.log(data);
          formik.setValues({
            templeName: data.templeName || "",
            templeLocation: data.templeLocation || "",
            templeDesc: data.templeDesc || "",
            templeImage: null,
          });
          setTempleImageUrl(data.templeImage || "");
          setOriginalTempleName(data.templeName || "");
          setNewImageSelected(false);
        } else {
          Swal.fire({
            icon: "error",
            text: res.message || "Failed to load temple details",
            ...SWAL_SETTINGS,
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log("Error fetching temple details:", err);
        setLoading(false);
      });
  }, [id]);

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">{t("Edit Temple")}</h6>
                <div className="row row-sm">
                  {/* Temple Name */}
                  <div className="col-md-6 form-group">
                    <label>{t("Temple Name")}:</label>
                    <input
                      type="text"
                      name="templeName"
                      className="form-control"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.templeName}
                    />
                    <span className="text-danger">
                      <CustomError name="templeName" form={formik} />
                    </span>
                  </div>

                  {/* Temple Location */}
                  <div className="col-md-6 form-group">
                    <label>{t("Temple Location")}:</label>
                    <input
                      type="text"
                      name="templeLocation"
                      className="form-control"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.templeLocation}
                    />
                    <span className="text-danger">
                      <CustomError name="templeLocation" form={formik} />
                    </span>
                  </div>

                  {/* Temple Description */}
                  <div className="col-md-12 form-group">
                    <label>{t("Temple Description")}:</label>
                    <textarea
                      name="templeDesc"
                      className="form-control"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.templeDesc}
                      rows={4}
                    />
                    <span className="text-danger">
                      <CustomError name="templeDesc" form={formik} />
                    </span>
                  </div>

                  {/* Temple Image */}
                  <div className="col-md-12 form-group">
                    <label>{t("Temple Image")}:</label>
                    <input
                      type="file"
                      name="templeImage"
                      className="form-control"
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={(e) => {
                        const file = e.currentTarget.files[0];
                        if (file) {
                          formik.setFieldValue("templeImage", file);
                          setNewImageSelected(true);
                          setTempleImageUrl(URL.createObjectURL(file)); // Blob preview
                        }
                      }}
                    />
                    <span className="text-danger">
                      <CustomError name="templeImage" form={formik} />
                    </span>
                    <div className="mt-3">
                      <img
                        src={
                          newImageSelected
                            ? URL.createObjectURL(formik.values.templeImage)
                            : templeImageUrl
                        }
                        alt={"Temple Image"}
                        style={{ maxWidth: "200px", height: "auto",border: "1px solid #ccc",borderRadius:"5px" }}
                      />
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="col-md-12 mt-3">
                    <button
                      type="submit"
                      className="btn btn-info mr-2"
                      disabled={submitted}
                    >
                      {t("btn_submit")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate(-1)}
                      disabled={submitted}
                    >
                      {t("btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default EditTemple;
